import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { AchievementProvider } from "@/contexts/AchievementContext";
import { FeaturesProvider } from "@/contexts/FeaturesContext";
import { ProtectedRoute, SubscriptionRoute, FreeTrialRoute, FeatureGatedRoute } from "@/components/ProtectedRoute";
import { ReminderBanner } from "@/components/ReminderBanner";
import { CookieBanner } from "@/components/CookieBanner";
import { ScrollToTop } from "@/components/ScrollToTop";
import InstallPrompt from "@/components/InstallPrompt";
import { setupTokenValidation } from "@/utils/tokenUtils";
import "@/i18n/config";
import "@/App.css";
import "@/styles/themes/prism3d.css";
import "@/styles/rolex-green-theme.css";

// Critical pages - load immediately
import DynamicHomePage from "@/pages/DynamicHomePage";
import LoginPage from "@/pages/LoginPage";

// Lazy load non-critical pages for faster initial load
const HomePage = lazy(() => import("@/pages/HomePage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const HomepageConfigPage = lazy(() => import("@/pages/HomepageConfigPage"));
const PracticesPage = lazy(() => import("@/pages/PracticesPage"));
const JournalPage = lazy(() => import("@/pages/JournalPage"));
const ProgressPage = lazy(() => import("@/pages/ProgressPage"));
const CalendarPage = lazy(() => import("@/pages/CalendarPage"));
const LearningPage = lazy(() => import("@/pages/LearningPage"));
const ProjectsPage = lazy(() => import("@/pages/ProjectsPage"));
const SubscriptionPage = lazy(() => import("@/pages/SubscriptionPage"));
const SubscriptionSuccessPage = lazy(() => import("@/pages/SubscriptionSuccessPage"));
const EnvironmentCheckPage = lazy(() => import("@/pages/EnvironmentCheckPage"));
const RelationshipReadinessPage = lazy(() => import("@/pages/RelationshipReadinessPage"));
const FocusTimePage = lazy(() => import("@/pages/FocusTimePage"));
const FocusGamesPage = lazy(() => import("@/pages/FocusGamesPage"));
const GoalCounterPage = lazy(() => import("@/pages/GoalCounterPage"));
const KontaktBasisPage = lazy(() => import("@/pages/KontaktBasisPage"));
const ChatbotPage = lazy(() => import("@/pages/ChatbotPage"));
const MusicRelaxationPage = lazy(() => import("@/pages/MusicRelaxationPage"));
const DailyRoutinePage = lazy(() => import("@/pages/DailyRoutinePage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const ImpressumPage = lazy(() => import("@/pages/ImpressumPage"));
const TermsOfServicePage = lazy(() => import("@/pages/TermsOfServicePage"));
const WiderrufsrechtPage = lazy(() => import("@/pages/WiderrufsrechtPage"));
const AffiliateDashboard = lazy(() => import("@/pages/AffiliateDashboard"));
const AffiliateApplicationPage = lazy(() => import("@/pages/AffiliateApplicationPage"));
const FavoriteQuotesPage = lazy(() => import("@/pages/FavoriteQuotesPage"));
const PlayInvitePage = lazy(() => import("@/pages/PlayInvitePage"));
const LuxuryTemptationsPage = lazy(() => import("@/pages/LuxuryTemptationsPage"));
const BudgetPage = lazy(() => import("@/pages/BudgetPage"));
const FeatureComparisonPage = lazy(() => import("@/pages/FeatureComparisonPage"));
const WiderrufsformularPage = lazy(() => import("@/pages/WiderrufsformularPage"));
const LeisurePlannerPage = lazy(() => import("@/pages/LeisurePlannerPage"));
const ColdCallTrainerPage = lazy(() => import("@/pages/ColdCallTrainerPage"));
const AdminApp = lazy(() => import("@/admin/AdminApp"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#006039]"></div>
  </div>
);

function App() {
  // Setup token validation on app start
  useEffect(() => {
    const intervalId = setupTokenValidation();
    return () => clearInterval(intervalId);
  }, []);

  // Hide the initial HTML splash screen when React is ready
  useEffect(() => {
    // Show splash for at least 2 seconds for better UX
    const timer = setTimeout(() => {
      if (window.hideInitialSplash) {
        window.hideInitialSplash();
      }
    }, 2000);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <HelmetProvider>
      <ThemeProvider>
        <FeaturesProvider>
        <SubscriptionProvider>
          <AchievementProvider>
          <div className="App min-h-screen bg-background text-foreground transition-colors duration-300">
            <BrowserRouter>
              <ScrollToTop />
              <Suspense fallback={<PageLoader />}>
          <ReminderBanner />
          <CookieBanner />
          <InstallPrompt />
          <Routes>
            {/* ============================================
                PUBLIC ROUTES - No login required
            ============================================ */}
            <Route path="/" element={<DynamicHomePage />} />
            <Route path="/home-classic" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/registrieren" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/passwort-vergessen" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/passwort-zuruecksetzen" element={<ResetPasswordPage />} />
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
            <Route path="/features" element={<FeatureComparisonPage />} />
            <Route path="/funktionen" element={<FeatureComparisonPage />} />
            
            {/* Legal Pages - Always accessible */}
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/datenschutz" element={<PrivacyPolicyPage />} />
            <Route path="/impressum" element={<ImpressumPage />} />
            <Route path="/legal-notice" element={<ImpressumPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/agb" element={<TermsOfServicePage />} />
            <Route path="/widerruf" element={<WiderrufsrechtPage />} />
            <Route path="/withdrawal" element={<WiderrufsrechtPage />} />
            <Route path="/widerrufsformular" element={<WiderrufsformularPage />} />
            <Route path="/withdrawal-form" element={<WiderrufsformularPage />} />
            
            {/* Game Invitation - Public (view-only for non-subscribers) */}
            <Route path="/play/:gameId" element={<PlayInvitePage />} />

            {/* ============================================
                FREE WITH LOGIN - Limited free access
            ============================================ */}
            {/* Practices - Free preview (login required) */}
            <Route path="/practices" element={
              <ProtectedRoute>
                <PracticesPage />
              </ProtectedRoute>
            } />
            
            {/* Focus Timer - 2 free uses, then subscription required */}
            <Route path="/focus" element={
              <FreeTrialRoute maxFreeUses={2} featureKey="focus_timer">
                <FocusTimePage />
              </FreeTrialRoute>
            } />

            {/* Goal Counter - Available for subscribers */}
            <Route path="/goal-counter" element={
              <SubscriptionRoute>
                <GoalCounterPage />
              </SubscriptionRoute>
            } />

            {/* Kontakt Basis - CRM (Premium Addon) */}
            <Route path="/kontakt-basis" element={
              <SubscriptionRoute>
                <KontaktBasisPage />
              </SubscriptionRoute>
            } />

            {/* Budget Tool (Premium) */}
            <Route path="/budget" element={
              <SubscriptionRoute>
                <BudgetPage />
              </SubscriptionRoute>
            } />

            {/* ============================================
                SUBSCRIPTION REQUIRED - Full features
            ============================================ */}
            <Route path="/journal" element={
              <SubscriptionRoute>
                <JournalPage />
              </SubscriptionRoute>
            } />
            <Route path="/tagebuch" element={
              <SubscriptionRoute>
                <JournalPage />
              </SubscriptionRoute>
            } />
            
            <Route path="/progress" element={
              <SubscriptionRoute>
                <ProgressPage />
              </SubscriptionRoute>
            } />
            
            <Route path="/calendar" element={
              <SubscriptionRoute>
                <CalendarPage />
              </SubscriptionRoute>
            } />
            
            <Route path="/learning" element={
              <SubscriptionRoute>
                <LearningPage />
              </SubscriptionRoute>
            } />
            
            <Route path="/projects" element={
              <SubscriptionRoute>
                <ProjectsPage />
              </SubscriptionRoute>
            } />
            <Route path="/leisure-planner" element={
              <SubscriptionRoute>
                <LeisurePlannerPage />
              </SubscriptionRoute>
            } />
            <Route path="/cold-call-trainer" element={
              <FeatureGatedRoute featureId="cold_call_trainer">
                <ColdCallTrainerPage />
              </FeatureGatedRoute>
            } />
            <Route path="/kaltakquise-trainer" element={
              <FeatureGatedRoute featureId="cold_call_trainer">
                <ColdCallTrainerPage />
              </FeatureGatedRoute>
            } />
            
            <Route path="/relationship-readiness" element={
              <SubscriptionRoute>
                <RelationshipReadinessPage />
              </SubscriptionRoute>
            } />
            
            <Route path="/focus/games" element={
              <SubscriptionRoute>
                <FocusGamesPage />
              </SubscriptionRoute>
            } />
            
            <Route path="/focus-games" element={
              <SubscriptionRoute>
                <FocusGamesPage />
              </SubscriptionRoute>
            } />
            
            <Route path="/chatbot" element={
              <SubscriptionRoute>
                <ChatbotPage />
              </SubscriptionRoute>
            } />
            
            <Route path="/music" element={
              <SubscriptionRoute>
                <MusicRelaxationPage />
              </SubscriptionRoute>
            } />
            
            <Route path="/daily-routine" element={
              <SubscriptionRoute>
                <DailyRoutinePage />
              </SubscriptionRoute>
            } />
            
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />
            
            <Route path="/favorite-quotes" element={
              <ProtectedRoute>
                <FavoriteQuotesPage />
              </ProtectedRoute>
            } />
            
            <Route path="/luxury" element={
              <SubscriptionRoute>
                <LuxuryTemptationsPage />
              </SubscriptionRoute>
            } />
            
            <Route path="/homepage-config" element={
              <ProtectedRoute>
                <HomepageConfigPage />
              </ProtectedRoute>
            } />
            
            <Route path="/environment" element={
              <ProtectedRoute>
                <EnvironmentCheckPage />
              </ProtectedRoute>
            } />
            
            {/* ============================================
                AFFILIATE ROUTES - Login required
            ============================================ */}
            <Route path="/partner" element={
              <ProtectedRoute>
                <AffiliateDashboard />
              </ProtectedRoute>
            } />
            <Route path="/partner/bewerben" element={<AffiliateApplicationPage />} />
            <Route path="/affiliate" element={
              <ProtectedRoute>
                <AffiliateDashboard />
              </ProtectedRoute>
            } />
            <Route path="/affiliate/apply" element={<AffiliateApplicationPage />} />
            
            {/* ============================================
                ADMIN ROUTES - Admin role required
            ============================================ */}
            <Route path="/admin/*" element={<AdminApp />} />
          </Routes>
          </Suspense>
          </BrowserRouter>
          <Toaster position="top-center" />
        </div>
        </AchievementProvider>
      </SubscriptionProvider>
        </FeaturesProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;